import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent {
  dropdownOpen = false;

  /** Drawer state, owned by the layout. Read only to pick the icon and label. */
  @Input() navOpen = false;
  @Output() toggleNav = new EventEmitter<void>();

  /**
   * stopPropagation because the document click handler above closes the user
   * dropdown — without it, opening the nav from a topbar button would also be
   * seen as a click outside that menu, which is harmless today but exactly the
   * kind of coupling that breaks when someone adds a second overlay.
   */
  onToggleNav(event: MouseEvent) {
    event.stopPropagation();
    this.toggleNav.emit();
  }

  constructor(public auth: AuthService, private router: Router) {}

  toggleDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.dropdownOpen = false;
  }

  logout() {
    this.dropdownOpen = false;
    this.auth.logout().subscribe({ error: () => this.auth.clearSession() });
  }

  getPageTitle(): string {
    const url = this.router.url.split('?')[0];
    const map: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/admins': 'Administrators',
      '/admins/new': 'New administrator',
      '/roles': 'Roles',
      '/roles/new': 'New role',
      '/clients': 'Clients',
      '/clients/new': 'New client',
      '/salespeople': 'Salespeople',
      '/sender-domains': 'Sender Domains',
      '/mail-providers': 'Mail Providers',
      '/mail-providers/new': 'New mail provider',
      '/salespeople/new': 'New salesperson',
      '/stores': 'Stores',
      '/plugins': 'Plugins',
      '/academy/categories': 'Academy — Categories',
      '/academy/videos': 'Academy — Videos',
      '/sms/settings': 'SMS Settings',
      '/sms/otp': 'OTP Logs',
      '/system-messages': 'System Messages',
      '/profile': 'My Profile',
    };
    if (map[url]) return map[url];
    // Checked before the generic /edit fallback below, which otherwise reads
    // every edit route as a client one.
    if (url.startsWith('/email-templates')) return 'Email Templates';
    if (url.startsWith('/chargebee-billing/coupons')) return 'Coupons';
    if (url.includes('/edit')) {
      if (url.includes('/admins/'))      return 'Edit administrator';
      if (url.includes('/roles/'))       return 'Edit role';
      if (url.includes('/salespeople/')) return 'Edit salesperson';
      if (url.includes('/mail-providers/')) return 'Edit mail provider';
      return 'Edit client';
    }
    if (url.includes('/websites')) return 'Client Websites';
    if (url.startsWith('/salespeople/')) return 'Salesperson';
    return 'Happy Ordering';
  }
}
